import React, { useEffect, useState } from 'react';
import { billsApi, BillDetails } from '../../api/billsApi';
import { BillInvoiceHtmlViewer } from '../bills/BillInvoiceHtmlViewer';
import { ReceiptHtmlViewer, printReceipt } from '../receipt/ReceiptHtmlViewer';
import { receiptPrintButtonLabel } from '../../utils/receiptPrintLabel';
import toast from 'react-hot-toast';

interface BillDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  billId: string;
}

export const BillDetailsModal: React.FC<BillDetailsModalProps> = ({
  isOpen,
  onClose,
  billId,
}) => {
  const [billDetails, setBillDetails] = useState<BillDetails | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && billId) {
      loadBillDetails();
    }
  }, [isOpen, billId]);

  const loadBillDetails = async () => {
    try {
      setLoading(true);
      const details = await billsApi.getBillDetails(billId);
      setBillDetails(details);
    } catch (error) {
      console.error('Error loading bill details:', error);
      toast.error('Lỗi khi tải chi tiết hóa đơn');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (!billId || billDetails?.status !== 'PAID') return;
    printReceipt(billId);
  };

  if (!isOpen) return null;

  const isPaid = billDetails?.status === 'PAID';
  const isCancelledReceipt =
    billDetails?.status === 'CANCELLED' && Boolean(billDetails.receiptNumber?.trim());
  const showReceiptView = isPaid || isCancelledReceipt;
  const title = showReceiptView ? 'Chi tiết phiếu thu' : 'Chi tiết hóa đơn';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[95vh] overflow-hidden flex flex-col">
        <div className="bg-white border-b px-6 py-4 flex justify-between items-center shrink-0">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
            {billDetails?.receiptNumber && showReceiptView && (
              <p className="text-sm text-gray-600 mt-1">
                Mã phiếu thu: <strong>{billDetails.receiptNumber}</strong>
                {isCancelledReceipt && (
                  <span className="ml-2 text-red-600 font-medium">(Đã huỷ)</span>
                )}
              </p>
            )}
            {billDetails && !showReceiptView && (
              <p className="text-sm text-gray-600 mt-1">
                Mã hóa đơn: <strong>{billDetails.billId}</strong>
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isPaid && (
              <button
                onClick={handlePrint}
                className="hidden sm:inline-flex items-center gap-1.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-3 py-1.5 rounded-lg text-sm transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                In
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto bg-gray-100 min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500" />
              <span className="ml-2 text-gray-600">Đang tải...</span>
            </div>
          ) : showReceiptView ? (
            <ReceiptHtmlViewer billId={billId} minHeight="75vh" />
          ) : billDetails ? (
            <BillInvoiceHtmlViewer billId={billId} minHeight="75vh" />
          ) : (
            <div className="text-center py-16 text-gray-500">
              Không tìm thấy thông tin hóa đơn
            </div>
          )}
        </div>

        <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 border-t shrink-0">
          <button
            onClick={onClose}
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg transition-colors"
          >
            Đóng
          </button>
          {isPaid && (
            <button
              onClick={handlePrint}
              className="bg-cyan-500 hover:bg-cyan-600 text-white px-4 py-2 rounded-lg transition-colors"
            >
              {receiptPrintButtonLabel(billDetails?.receiptPrintCopyCount)}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
